// =============================================================================
// Admin Distributions Management Page
// =============================================================================

import { useState, useEffect, useCallback } from 'react';
import { PencilIcon, XMarkIcon, TrashIcon, ArrowDownTrayIcon, EllipsisVerticalIcon, CheckCircleIcon, EnvelopeIcon, ShieldExclamationIcon } from '@heroicons/react/24/outline';
import { adminService, type DealWithStats, type ShareClass } from '../../services/admin.service';
import api, { getApiError } from '../../services/api';
import { Button, DistributionEmailModal } from '../../components/ui';
import { usePermissions } from '../../hooks/usePermissions';

// =============================================================================
// Types
// =============================================================================

interface Distribution {
  id: string;
  dealId: string;
  dealName: string;
  type: string;
  totalAmount: number;
  distributionDate: string;
  paymentMethod: string;
  status: 'DRAFT' | 'PUBLISHED';
  notes?: string;
  allocations: {
    investorName: string;
    amount: number;
    ownershipPercentage: number;
    shareClassId: string | null;
  }[];
}

// =============================================================================
// Component
// =============================================================================

export function DistributionsPage() {
  const { hasPermission, isLoaded: permsLoaded } = usePermissions();
  const canCreate = hasPermission('distributions', 'create');
  const canEdit = hasPermission('distributions', 'edit');
  const canDelete = hasPermission('distributions', 'delete');
  const canViewSensitive = hasPermission('investors', 'sensitive');
  const [distributions, setDistributions] = useState<Distribution[]>([]);
  const [deals, setDeals] = useState<DealWithStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [selectedDeal, setSelectedDeal] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [selectedStatus, setSelectedStatus] = useState<'' | 'DRAFT' | 'PUBLISHED'>('');
  const [dateRange, setDateRange] = useState<'all' | '30' | '90' | '365'>('all');

  // Modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [editingDistribution, setEditingDistribution] = useState<Distribution | null>(null);
  const [showNachaModal, setShowNachaModal] = useState<Distribution | null>(null);
  const [publishingId, setPublishingId] = useState<string | null>(null);
  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null);
  const [dropdownPos, setDropdownPos] = useState<{ top: number; right: number }>({ top: 0, right: 0 });
  const [confirmPublishDist, setConfirmPublishDist] = useState<Distribution | null>(null);
  const [emailModalData, setEmailModalData] = useState<{
    distributionId: string;
    dealName: string;
    totalAmount: number;
    distributionDate: string;
    type: string;
    investorCount: number;
  } | null>(null);
  const [notifyOnPublish, setNotifyOnPublish] = useState(true);

  // Form state
  const [formDealId, setFormDealId] = useState('');
  const [formType, setFormType] = useState('QUARTERLY');
  const [formAmount, setFormAmount] = useState('');
  const [formAmountDisplay, setFormAmountDisplay] = useState('');
  const [formDate, setFormDate] = useState(new Date().toISOString().split('T')[0]);
  const [formPaymentMethod, setFormPaymentMethod] = useState('ACH');
  const [formNotes, setFormNotes] = useState('');
  const [formShareClasses, setFormShareClasses] = useState<ShareClass[]>([]);
  const [selectedShareClassIds, setSelectedShareClassIds] = useState<string[]>([]);
  const [loadingShareClasses, setLoadingShareClasses] = useState(false);

  // Load share classes when deal changes in create form
  useEffect(() => {
    if (!formDealId) {
      setFormShareClasses([]);
      setSelectedShareClassIds([]);
      return;
    }
    const loadShareClasses = async () => {
      try {
        setLoadingShareClasses(true);
        const classes = await adminService.getShareClasses(formDealId);
        setFormShareClasses(classes);
      } catch {
        setFormShareClasses([]);
      } finally {
        setLoadingShareClasses(false);
      }
    };
    loadShareClasses();
  }, [formDealId]);

  // Format amount with commas as user types
  const handleAmountChange = (value: string) => {
    // Remove all non-numeric characters except decimal
    const numericValue = value.replace(/[^0-9.]/g, '');

    // Store the raw numeric value
    setFormAmount(numericValue);

    // Format with commas for display
    if (numericValue) {
      const parts = numericValue.split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      setFormAmountDisplay(parts.join('.'));
    } else {
      setFormAmountDisplay('');
    }
  };

  const distributionTypes = [
    { value: 'QUARTERLY', label: 'Quarterly' },
    { value: 'ANNUAL', label: 'Annual' },
    { value: 'SPECIAL', label: 'Special' },
    { value: 'RETURN_OF_CAPITAL', label: 'Return of Capital' },
    { value: 'REFINANCE', label: 'Refinance' },
    { value: 'SALE', label: 'Sale' },
    { value: 'OTHER', label: 'Other' },
  ];

  const paymentMethods = [
    { value: 'ACH', label: 'ACH Transfer' },
    { value: 'CHECK', label: 'Check' },
    { value: 'WIRE', label: 'Wire Transfer' },
    { value: 'PLAID_ACH', label: 'Plaid ACH (coming soon)', disabled: true },
  ];

  useEffect(() => {
    loadDeals();
    loadDistributions();
  }, []);

  useEffect(() => {
    loadDistributions();
  }, [selectedDeal, selectedType, selectedStatus, dateRange]);

  // Close dropdown when clicking outside or scrolling
  useEffect(() => {
    if (!openDropdownId) return;
    const handleClick = () => setOpenDropdownId(null);
    const handleScroll = () => setOpenDropdownId(null);
    document.addEventListener('click', handleClick);
    window.addEventListener('scroll', handleScroll, true);
    return () => {
      document.removeEventListener('click', handleClick);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [openDropdownId]);

  const openDropdown = useCallback((e: React.MouseEvent, distId: string) => {
    e.stopPropagation();
    if (openDropdownId === distId) {
      setOpenDropdownId(null);
      return;
    }
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    setDropdownPos({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
    setOpenDropdownId(distId);
  }, [openDropdownId]);

  const loadDeals = async () => {
    try {
      const data = await adminService.listDeals();
      setDeals(data);
    } catch (err) {
      console.error('Failed to load deals:', err);
    }
  };

  const loadDistributions = async () => {
    try {
      setLoading(true);
      setError(null);

      // Build query params
      const params = new URLSearchParams();
      if (selectedDeal) params.append('dealId', selectedDeal);
      if (selectedType) params.append('type', selectedType);
      if (selectedStatus) params.append('status', selectedStatus);
      if (dateRange !== 'all') {
        const startDate = new Date();
        startDate.setDate(startDate.getDate() - parseInt(dateRange));
        params.append('startDate', startDate.toISOString());
      }

      const response = await api.get<{ success: boolean; data: Distribution[] }>(
        `/admin/distributions?${params.toString()}`
      );
      setDistributions(response.data.data || []);
    } catch (err) {
      setError('Failed to load distributions');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateDistribution = async () => {
    if (!formDealId || !formAmount || !formDate) {
      setCreateError('Please fill in all required fields');
      return;
    }

    try {
      setCreating(true);
      setCreateError(null);

      await api.post('/admin/distributions', {
        dealId: formDealId,
        type: formType,
        totalAmount: parseFloat(formAmount),
        distributionDate: formDate,
        paymentMethod: formPaymentMethod,
        notes: formNotes || undefined,
        shareClassIds: selectedShareClassIds.length > 0 ? selectedShareClassIds : undefined,
      });

      setShowCreateModal(false);
      resetForm();
      loadDistributions();
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: string } } };
      setCreateError(error.response?.data?.error || 'Failed to record distribution');
    } finally {
      setCreating(false);
    }
  };

  const resetForm = () => {
    setFormDealId('');
    setFormType('QUARTERLY');
    setFormAmount('');
    setFormAmountDisplay('');
    setFormDate(new Date().toISOString().split('T')[0]);
    setFormPaymentMethod('ACH');
    setFormNotes('');
    setFormShareClasses([]);
    setSelectedShareClassIds([]);
    setCreateError(null);
  };

  const handlePublish = async (distributionId: string, sendNotification: boolean = false) => {
    try {
      setPublishingId(distributionId);
      const response = await api.post<{
        success: boolean;
        data: {
          id: string;
          dealName: string;
          type: string;
          totalAmount: number;
          distributionDate: string;
          status: string;
          allocationCount: number;
        };
      }>(`/admin/distributions/${distributionId}/publish`);

      // Auto-send email notification if toggle is on
      if (response.data.success && response.data.data && sendNotification) {
        const published = response.data.data;
        const subject = `Distribution Processed: ${published.dealName}`;
        const typeLabel = getTypeLabel(published.type).toLowerCase();
        const dateStr = formatDate(published.distributionDate);
        const message = `Your ${typeLabel} distribution for ${published.dealName} has been processed and is on its way. The distribution was recorded on ${dateStr}.\n\nPlease log into your investor portal to view the full details of this distribution.`;

        try {
          const notifyResponse = await api.post(`/admin/distributions/${published.id}/notify`, {
            subject,
            message,
          });
          if (notifyResponse.data.success) {
            const { successCount, failCount } = notifyResponse.data.data;
            if (failCount > 0) {
              alert(`Distribution published. Sent ${successCount} notification(s), ${failCount} failed.`);
            }
          }
        } catch {
          alert('Distribution published successfully, but email notifications failed to send.');
        }
      }

      loadDistributions();
    } catch (err) {
      const error = getApiError(err);
      alert(`Failed to publish: ${error.message}`);
    } finally {
      setPublishingId(null);
    }
  };

  const handleSendDistributionEmail = async (subject: string, message: string) => {
    if (!emailModalData) return;

    const response = await api.post(`/admin/distributions/${emailModalData.distributionId}/notify`, {
      subject,
      message,
    });

    if (!response.data.success) {
      throw new Error('Failed to send notifications');
    }

    const { successCount, failCount } = response.data.data;
    if (failCount > 0) {
      alert(`Sent ${successCount} notification(s). ${failCount} failed to send.`);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      timeZone: 'UTC',
    });
  };

  const getTypeLabel = (type: string) => {
    const found = distributionTypes.find((t) => t.value === type);
    return found?.label || type;
  };

  const getPaymentMethodLabel = (method: string) => {
    const found = paymentMethods.find((m) => m.value === method);
    return found?.label || method;
  };

  // Calculate totals
  const totalDistributed = distributions.reduce((sum, d) => sum + d.totalAmount, 0);

  // Permission guard - early return after all hooks
  if (permsLoaded && !hasPermission('distributions', 'view')) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <ShieldExclamationIcon className="h-12 w-12 text-gray-400 mb-3" />
        <h3 className="text-lg font-medium text-gray-900">Access Denied</h3>
        <p className="mt-1 text-sm text-gray-500">You do not have permission to view this page.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Distributions</h1>
          <p className="text-gray-500 mt-1">Record and manage investor distributions</p>
        </div>
        {canCreate && (
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            Record Distribution
          </button>
        )}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-sm text-gray-500">Total Distributions</p>
          <p className="text-2xl font-bold text-green-600">{formatCurrency(totalDistributed)}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-sm text-gray-500">Distribution Count</p>
          <p className="text-2xl font-bold text-gray-900">{distributions.length}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-sm text-gray-500">Active Deals</p>
          <p className="text-2xl font-bold text-gray-900">{deals.filter(d => d.isActive).length}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Deal</label>
            <select
              value={selectedDeal}
              onChange={(e) => setSelectedDeal(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">All Deals</option>
              {deals.map((deal) => (
                <option key={deal.id} value={deal.id}>{deal.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
            <select
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">All Types</option>
              {distributionTypes.map((type) => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value as typeof selectedStatus)}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">All Status</option>
              <option value="DRAFT">Draft</option>
              <option value="PUBLISHED">Published</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Date Range</label>
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value as typeof dateRange)}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="all">All Time</option>
              <option value="30">Last 30 Days</option>
              <option value="90">Last 90 Days</option>
              <option value="365">Last Year</option>
            </select>
          </div>
          <div className="flex items-end">
            <button
              onClick={() => {
                setSelectedDeal('');
                setSelectedType('');
                setSelectedStatus('');
                setDateRange('all');
              }}
              className="px-4 py-2 text-gray-600 hover:text-gray-900"
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Distributions List */}
      <div className="bg-white rounded-lg shadow">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-900" />
          </div>
        ) : error ? (
          <div className="text-center py-12 text-red-600">{error}</div>
        ) : distributions.length === 0 ? (
          <div className="text-center py-12">
            <svg className="w-12 h-12 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-gray-500">No distributions found</p>
            {canCreate && (
              <button
                onClick={() => setShowCreateModal(true)}
                className="mt-4 text-primary-600 hover:text-primary-800"
              >
                Record your first distribution
              </button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Deal</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Payment</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {distributions.map((dist) => (
                  <tr key={dist.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap font-medium">
                      {formatDate(dist.distributionDate)}
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-gray-900">{dist.dealName}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-sm">
                        {getTypeLabel(dist.type)}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right font-semibold text-green-600">
                      {formatCurrency(dist.totalAmount)}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 rounded text-sm ${
                        dist.status === 'DRAFT'
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-green-100 text-green-800'
                      }`}>
                        {dist.status === 'DRAFT' ? 'Draft' : 'Published'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-gray-500">
                      {getPaymentMethodLabel(dist.paymentMethod)}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <button
                        onClick={(e) => openDropdown(e, dist.id)}
                        className="p-1 rounded-lg hover:bg-gray-100 text-gray-500 hover:text-gray-700"
                      >
                        <EllipsisVerticalIcon className="h-5 w-5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Actions Dropdown (fixed position, outside table overflow) */}
      {openDropdownId && (() => {
        const dist = distributions.find(d => d.id === openDropdownId);
        if (!dist) return null;
        return (
          <div
            className="fixed w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50"
            style={{ top: dropdownPos.top, right: dropdownPos.right }}
          >
            {dist.paymentMethod === 'ACH' && canViewSensitive && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setOpenDropdownId(null);
                  setShowNachaModal(dist);
                }}
                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
              >
                <ArrowDownTrayIcon className="h-4 w-4" />
                Export NACHA File
              </button>
            )}
            {dist.status === 'DRAFT' && canEdit && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setOpenDropdownId(null);
                  setConfirmPublishDist(dist);
                }}
                className="w-full text-left px-4 py-2 text-sm text-green-700 hover:bg-green-50 flex items-center gap-2"
                disabled={publishingId === dist.id}
              >
                <CheckCircleIcon className="h-4 w-4" />
                {publishingId === dist.id ? 'Publishing...' : 'Publish Distribution'}
              </button>
            )}
            {canEdit && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setOpenDropdownId(null);
                  setEditingDistribution(dist);
                }}
                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
              >
                <PencilIcon className="h-4 w-4" />
                Edit Distribution
              </button>
            )}
          </div>
        );
      })()}

      {/* Create Distribution Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold">Record Distribution</h2>
                <button
                  onClick={() => {
                    setShowCreateModal(false);
                    resetForm();
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {createError && (
                <div className="mb-4 p-3 bg-red-50 text-red-600 rounded-lg text-sm">
                  {createError}
                </div>
              )}

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Deal *</label>
                  <select
                    value={formDealId}
                    onChange={(e) => setFormDealId(e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    required
                  >
                    <option value="">Select a deal</option>
                    {deals.map((deal) => (
                      <option key={deal.id} value={deal.id}>{deal.name}</option>
                    ))}
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Type *</label>
                    <select
                      value={formType}
                      onChange={(e) => setFormType(e.target.value)}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      {distributionTypes.map((type) => (
                        <option key={type.value} value={type.value}>{type.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Payment Method</label>
                    <select
                      value={formPaymentMethod}
                      onChange={(e) => setFormPaymentMethod(e.target.value)}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      {paymentMethods.map((method) => (
                        <option key={method.value} value={method.value} disabled={method.disabled}>{method.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Total Amount *</label>
                    <div className="relative">
                      <span className="absolute left-3 top-2 text-gray-500">$</span>
                      <input
                        type="text"
                        value={formAmountDisplay}
                        onChange={(e) => handleAmountChange(e.target.value)}
                        placeholder="0.00"
                        className="w-full pl-7 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Distribution Date *</label>
                    <input
                      type="date"
                      value={formDate}
                      onChange={(e) => setFormDate(e.target.value)}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                  <textarea
                    value={formNotes}
                    onChange={(e) => setFormNotes(e.target.value)}
                    placeholder="Optional notes about this distribution"
                    rows={3}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                {/* Share Class Selection */}
                {formDealId && !loadingShareClasses && formShareClasses.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Share Classes</label>
                    <div className="space-y-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
                      {formShareClasses.map((sc) => (
                        <label key={sc.id} className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={selectedShareClassIds.includes(sc.id)}
                            onChange={() => {
                              setSelectedShareClassIds(prev =>
                                prev.includes(sc.id) ? prev.filter(x => x !== sc.id) : [...prev, sc.id]
                              );
                            }}
                            className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                          />
                          <span className="text-sm text-gray-900">{sc.name}</span>
                          <span className="text-xs text-gray-500">({sc.code})</span>
                        </label>
                      ))}
                    </div>
                    <p className="mt-1 text-xs text-gray-500">
                      {selectedShareClassIds.length === 0
                        ? 'All investors will receive allocations (no filter)'
                        : `Only investors in selected class${selectedShareClassIds.length > 1 ? 'es' : ''} will receive allocations (pro rata)`}
                    </p>
                  </div>
                )}
                {formDealId && loadingShareClasses && (
                  <div className="text-sm text-gray-500">Loading share classes...</div>
                )}

                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  <p className="text-sm text-yellow-800">
                    <strong>Note:</strong> This will automatically calculate and allocate distributions to {selectedShareClassIds.length > 0 ? 'investors in the selected share classes' : 'all investors'} based on their ownership percentages.
                  </p>
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
                <button
                  onClick={() => {
                    setShowCreateModal(false);
                    resetForm();
                  }}
                  className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                  disabled={creating}
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateDistribution}
                  disabled={creating || !formDealId || !formAmount || !formDate}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {creating ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                      Recording...
                    </>
                  ) : (
                    'Record Distribution'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Distribution Modal */}
      {editingDistribution && (
        <EditDistributionModal
          distribution={editingDistribution}
          distributionTypes={distributionTypes}
          paymentMethods={paymentMethods}
          canDelete={canDelete}
          onClose={() => setEditingDistribution(null)}
          onSaved={async () => {
            setEditingDistribution(null);
            await loadDistributions();
          }}
        />
      )}

      {/* NACHA Export Modal */}
      {showNachaModal && (
        <NachaExportModal
          distribution={showNachaModal}
          onClose={() => setShowNachaModal(null)}
        />
      )}

      {/* Distribution Email Notification Modal */}
      {emailModalData && (
        <DistributionEmailModal
          isOpen={!!emailModalData}
          onClose={() => setEmailModalData(null)}
          onSend={handleSendDistributionEmail}
          distribution={emailModalData}
        />
      )}

      {/* Publish Confirmation Modal */}
      {confirmPublishDist && (
        <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto">
          <div className="absolute inset-0 bg-black/50" onClick={() => setConfirmPublishDist(null)} />
          <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Publish Distribution</h2>
              <button onClick={() => setConfirmPublishDist(null)} className="p-1 hover:bg-gray-100 rounded-lg">
                <XMarkIcon className="h-5 w-5 text-gray-500" />
              </button>
            </div>

            <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm text-yellow-800 font-medium mb-2">Are you sure you want to publish this distribution?</p>
              <p className="text-sm text-yellow-700">Once published, investors will be able to see this distribution in their portal.</p>
            </div>

            <div className="mb-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Deal</span>
                <span className="font-medium text-gray-900">{confirmPublishDist.dealName}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Type</span>
                <span className="font-medium text-gray-900">{getTypeLabel(confirmPublishDist.type)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Amount</span>
                <span className="font-semibold text-green-600">{formatCurrency(confirmPublishDist.totalAmount)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Date</span>
                <span className="font-medium text-gray-900">{formatDate(confirmPublishDist.distributionDate)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Investors</span>
                <span className="font-medium text-gray-900">{confirmPublishDist.allocations.length}</span>
              </div>
            </div>

            {/* Email notification toggle */}
            <div className="mb-6 p-3 bg-gray-50 rounded-lg border border-gray-200">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={notifyOnPublish}
                  onChange={(e) => setNotifyOnPublish(e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <EnvelopeIcon className="h-5 w-5 text-gray-500 flex-shrink-0" />
                <div>
                  <span className="text-sm font-medium text-gray-900">Send email notification</span>
                  <p className="text-xs text-gray-500">Notify {confirmPublishDist.allocations.length} investor{confirmPublishDist.allocations.length !== 1 ? 's' : ''} by email</p>
                </div>
              </label>
            </div>

            <div className="flex gap-3">
              <Button
                type="button"
                variant="secondary"
                className="flex-1"
                onClick={() => setConfirmPublishDist(null)}
                disabled={publishingId === confirmPublishDist.id}
              >
                Cancel
              </Button>
              <Button
                type="button"
                className="flex-1 bg-green-600 hover:bg-green-700"
                onClick={() => {
                  handlePublish(confirmPublishDist.id, notifyOnPublish);
                  setConfirmPublishDist(null);
                }}
                disabled={publishingId === confirmPublishDist.id}
              >
                {publishingId === confirmPublishDist.id ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                    Publishing...
                  </>
                ) : (
                  <>
                    <CheckCircleIcon className="h-4 w-4 mr-2" />
                    Publish
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// Edit Distribution Modal
// =============================================================================

interface EditDistributionModalProps {
  distribution: Distribution;
  distributionTypes: { value: string; label: string }[];
  paymentMethods: { value: string; label: string; disabled?: boolean }[];
  canDelete: boolean;
  onClose: () => void;
  onSaved: () => void;
}

function EditDistributionModal({
  distribution,
  distributionTypes,
  paymentMethods,
  canDelete,
  onClose,
  onSaved,
}: EditDistributionModalProps) {
  const [formData, setFormData] = useState({
    type: distribution.type,
    totalAmount: distribution.totalAmount,
    distributionDate: distribution.distributionDate.split('T')[0],
    paymentMethod: distribution.paymentMethod,
    notes: distribution.notes || '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');

  // Share class editing state
  const [editShareClasses, setEditShareClasses] = useState<ShareClass[]>([]);
  const [editSelectedShareClassIds, setEditSelectedShareClassIds] = useState<string[]>([]);
  const [loadingEditShareClasses, setLoadingEditShareClasses] = useState(false);
  const [shareClassesLoaded, setShareClassesLoaded] = useState(false);

  // Load share classes for this distribution's deal and derive initial selection
  useEffect(() => {
    const loadShareClasses = async () => {
      try {
        setLoadingEditShareClasses(true);
        const classes = await adminService.getShareClasses(distribution.dealId);
        setEditShareClasses(classes);

        // Derive currently selected share class IDs from existing allocations
        const currentIds = new Set<string>();
        for (const alloc of distribution.allocations) {
          if (alloc.shareClassId) {
            currentIds.add(alloc.shareClassId);
          }
        }

        // If all share classes are represented (or no filtering was applied), leave empty (= all)
        // If only a subset is represented, pre-select those
        if (currentIds.size > 0 && currentIds.size < classes.length) {
          setEditSelectedShareClassIds(Array.from(currentIds));
        }

        setShareClassesLoaded(true);
      } catch {
        setEditShareClasses([]);
        setShareClassesLoaded(true);
      } finally {
        setLoadingEditShareClasses(false);
      }
    };
    loadShareClasses();
  }, [distribution.dealId, distribution.allocations]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      await adminService.updateDistribution(distribution.id, {
        type: formData.type,
        totalAmount: formData.totalAmount,
        distributionDate: formData.distributionDate,
        paymentMethod: formData.paymentMethod,
        notes: formData.notes || undefined,
        shareClassIds: shareClassesLoaded ? editSelectedShareClassIds : undefined,
      });
      await onSaved();
    } catch (err) {
      setError(getApiError(err).message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteClick = () => {
    setDeleteConfirmText('');
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    if (deleteConfirmText.toLowerCase() !== 'proceed') return;

    setIsSubmitting(true);
    try {
      await adminService.deleteDistribution(distribution.id);
      await onSaved();
    } catch (err) {
      setError(getApiError(err).message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 my-8 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Edit Distribution</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg">
            <XMarkIcon className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        <div className="mb-4 p-3 bg-gray-50 rounded-lg">
          <p className="text-sm font-medium text-gray-900">{distribution.dealName}</p>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Distribution Type *</label>
            <select
              value={formData.type}
              onChange={(e) => setFormData({ ...formData, type: e.target.value })}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            >
              {distributionTypes.map((type) => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Total Amount *</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
              <input
                type="text"
                inputMode="numeric"
                value={formData.totalAmount ? formData.totalAmount.toLocaleString('en-US') : ''}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^0-9.]/g, '');
                  setFormData({ ...formData, totalAmount: parseFloat(value) || 0 });
                }}
                required
                className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <p className="mt-1 text-xs text-gray-500">
              Changing the amount will recalculate all investor allocations.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Distribution Date *</label>
            <input
              type="date"
              value={formData.distributionDate}
              onChange={(e) => setFormData({ ...formData, distributionDate: e.target.value })}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Payment Method *</label>
            <select
              value={formData.paymentMethod}
              onChange={(e) => setFormData({ ...formData, paymentMethod: e.target.value })}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            >
              {paymentMethods.map((method) => (
                <option key={method.value} value={method.value}>{method.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            />
          </div>

          {/* Share Class Selection */}
          {!loadingEditShareClasses && editShareClasses.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Share Classes</label>
              <div className="space-y-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
                {editShareClasses.map((sc) => (
                  <label key={sc.id} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={editSelectedShareClassIds.includes(sc.id)}
                      onChange={() => {
                        setEditSelectedShareClassIds(prev =>
                          prev.includes(sc.id) ? prev.filter(x => x !== sc.id) : [...prev, sc.id]
                        );
                      }}
                      className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="text-sm text-gray-900">{sc.name}</span>
                    <span className="text-xs text-gray-500">({sc.code})</span>
                  </label>
                ))}
              </div>
              <p className="mt-1 text-xs text-gray-500">
                {editSelectedShareClassIds.length === 0
                  ? 'All investors will receive allocations (no filter)'
                  : `Only investors in selected class${editSelectedShareClassIds.length > 1 ? 'es' : ''} will receive allocations`}
              </p>
              <p className="mt-0.5 text-xs text-amber-600">
                Changing share classes will recalculate all investor allocations.
              </p>
            </div>
          )}

          <div className="flex gap-3 pt-4">
            {canDelete && (
              <Button
                type="button"
                variant="outline"
                onClick={handleDeleteClick}
                disabled={isSubmitting}
                className="text-red-600 hover:bg-red-50"
              >
                <TrashIcon className="h-4 w-4" />
              </Button>
            )}
            <Button type="button" variant="secondary" className="flex-1" onClick={onClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" className="flex-1" disabled={isSubmitting}>
              {isSubmitting ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </form>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-red-100 rounded-full">
                <TrashIcon className="w-6 h-6 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Delete Distribution</h3>
            </div>

            <p className="text-sm text-gray-600 mb-2">
              You are about to permanently delete this distribution:
            </p>
            <p className="text-sm font-medium text-gray-900 mb-1 p-2 bg-gray-50 rounded-lg">
              {distribution.dealName}
            </p>
            <p className="text-sm text-red-600 mb-4">
              This will also delete all allocation records and cannot be undone.
            </p>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Type <span className="font-bold">proceed</span> below to confirm deletion:
              </label>
              <input
                type="text"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder="Type 'proceed' to confirm"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                autoFocus
              />
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setDeleteConfirmText('');
                }}
                className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteConfirm}
                disabled={deleteConfirmText.toLowerCase() !== 'proceed' || isSubmitting}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                    Deleting...
                  </>
                ) : (
                  'Delete Distribution'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// NACHA Export Modal
// =============================================================================

interface NachaExportModalProps {
  distribution: Distribution;
  onClose: () => void;
}

function NachaExportModal({ distribution, onClose }: NachaExportModalProps) {
  const [formData, setFormData] = useState({
    companyName: '',
    companyId: '',
    bankRoutingNumber: '',
    bankName: '',
    effectiveEntryDate: new Date(Date.now() + 2 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 2 days from now
  });
  const [isExporting, setIsExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [routingError, setRoutingError] = useState<string | null>(null);
  const [dateWarning, setDateWarning] = useState<string | null>(null);

  // ABA routing number check digit validation (weights: 3,7,1,3,7,1,3,7,1)
  const validateRoutingNumber = (routing: string): boolean => {
    if (!/^\d{9}$/.test(routing)) return false;
    const weights = [3, 7, 1, 3, 7, 1, 3, 7, 1];
    let sum = 0;
    for (let i = 0; i < 9; i++) {
      sum += parseInt(routing[i], 10) * weights[i];
    }
    return sum % 10 === 0;
  };

  // Federal holidays for ACH business day check
  const getFederalHolidays = (year: number): Set<string> => {
    const holidays = new Set<string>();
    const fmt = (m: number, d: number) =>
      `${year}-${m.toString().padStart(2, '0')}-${d.toString().padStart(2, '0')}`;
    holidays.add(fmt(1, 1)); holidays.add(fmt(6, 19)); holidays.add(fmt(7, 4));
    holidays.add(fmt(11, 11)); holidays.add(fmt(12, 25));
    const nthWeekday = (month: number, weekday: number, n: number) => {
      const first = new Date(year, month - 1, 1);
      let day = 1 + ((weekday - first.getDay() + 7) % 7);
      return day + (n - 1) * 7;
    };
    const lastMonday = (month: number) => {
      const last = new Date(year, month, 0);
      return last.getDate() - ((last.getDay() - 1 + 7) % 7);
    };
    holidays.add(fmt(1, nthWeekday(1, 1, 3)));
    holidays.add(fmt(2, nthWeekday(2, 1, 3)));
    holidays.add(fmt(5, lastMonday(5)));
    holidays.add(fmt(9, nthWeekday(9, 1, 1)));
    holidays.add(fmt(10, nthWeekday(10, 1, 2)));
    holidays.add(fmt(11, nthWeekday(11, 4, 4)));
    const adjusted = new Set<string>();
    for (const h of holidays) {
      const d = new Date(h + 'T12:00:00');
      const dow = d.getDay();
      if (dow === 6) { d.setDate(d.getDate() - 1); adjusted.add(d.toISOString().split('T')[0]); }
      else if (dow === 0) { d.setDate(d.getDate() + 1); adjusted.add(d.toISOString().split('T')[0]); }
      else { adjusted.add(h); }
    }
    return adjusted;
  };

  const isBusinessDay = (dateStr: string): boolean => {
    const d = new Date(dateStr + 'T12:00:00');
    const dow = d.getDay();
    if (dow === 0 || dow === 6) return false;
    return !getFederalHolidays(d.getFullYear()).has(dateStr);
  };

  // Validate routing number on change
  const handleRoutingChange = (value: string) => {
    const cleaned = value.replace(/\D/g, '').substring(0, 9);
    setFormData({ ...formData, bankRoutingNumber: cleaned });
    if (cleaned.length === 9) {
      if (!validateRoutingNumber(cleaned)) {
        setRoutingError('Invalid routing number (check digit failed). Please verify.');
      } else {
        setRoutingError(null);
      }
    } else {
      setRoutingError(null);
    }
  };

  // Validate effective date on change
  const handleDateChange = (value: string) => {
    setFormData({ ...formData, effectiveEntryDate: value });
    if (value && !isBusinessDay(value)) {
      const d = new Date(value + 'T12:00:00');
      const dow = d.getDay();
      if (dow === 0 || dow === 6) {
        setDateWarning('This date falls on a weekend. ACH does not process on weekends.');
      } else {
        setDateWarning('This date falls on a federal holiday. ACH does not process on federal holidays.');
      }
    } else {
      setDateWarning(null);
    }
  };

  const formatDateForNacha = (dateString: string): string => {
    const date = new Date(dateString);
    const yy = date.getUTCFullYear().toString().slice(-2);
    const mm = (date.getUTCMonth() + 1).toString().padStart(2, '0');
    const dd = date.getUTCDate().toString().padStart(2, '0');
    return `${yy}${mm}${dd}`;
  };

  const handleExport = async () => {
    setError(null);

    if (!formData.companyName.trim()) {
      setError('Company name is required');
      return;
    }
    if (!formData.companyId.trim() || formData.companyId.replace(/\D/g, '').length !== 9) {
      setError('Company ID must be a 9-digit tax ID (FEIN)');
      return;
    }
    const cleanRouting = formData.bankRoutingNumber.replace(/\D/g, '');
    if (cleanRouting.length !== 9) {
      setError('Bank routing number must be 9 digits');
      return;
    }
    if (!validateRoutingNumber(cleanRouting)) {
      setError('Bank routing number failed check digit validation. Please verify the number.');
      return;
    }
    if (!formData.bankName.trim()) {
      setError('Bank name is required (e.g., "JPMORGAN CHASE")');
      return;
    }
    if (!formData.effectiveEntryDate) {
      setError('Effective entry date is required');
      return;
    }

    try {
      setIsExporting(true);

      const cleanCompanyId = '1' + formData.companyId.replace(/\D/g, '');
      const originatingDFI = cleanRouting.substring(0, 8);

      const response = await api.post(
        `/admin/distributions/${distribution.id}/export-nacha`,
        {
          companyName: formData.companyName.substring(0, 16).toUpperCase(),
          companyId: cleanCompanyId,
          originatingDFI,
          bankRoutingNumber: cleanRouting,
          bankName: formData.bankName.substring(0, 23).toUpperCase(),
          effectiveEntryDate: formatDateForNacha(formData.effectiveEntryDate),
        },
        {
          responseType: 'blob',
        }
      );

      const blob = new Blob([response.data], { type: 'text/plain' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `NACHA_${distribution.dealName.replace(/[^a-zA-Z0-9]/g, '_')}_${formatDateForNacha(formData.effectiveEntryDate)}.txt`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      onClose();
    } catch (err) {
      const error = getApiError(err);
      setError(error.message);
    } finally {
      setIsExporting(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(amount);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 my-8 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Export NACHA File</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg">
            <XMarkIcon className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        <div className="mb-4 p-3 bg-blue-50 rounded-lg">
          <p className="text-sm font-medium text-blue-900">{distribution.dealName}</p>
          <p className="text-sm text-blue-700">
            Total Amount: {formatCurrency(distribution.totalAmount)} | {distribution.allocations.length} investor(s)
          </p>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Company Name *</label>
            <input
              type="text"
              value={formData.companyName}
              onChange={(e) => setFormData({ ...formData, companyName: e.target.value.toUpperCase() })}
              maxLength={16}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              placeholder="Company Name"
            />
            <p className="mt-1 text-xs text-gray-500">Max 16 characters, uppercase</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Company Tax ID (FEIN) *</label>
            <input
              type="text"
              value={formData.companyId}
              onChange={(e) => setFormData({ ...formData, companyId: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              placeholder="XX-XXXXXXX"
            />
            <p className="mt-1 text-xs text-gray-500">9-digit federal employer identification number</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Bank Name *</label>
            <input
              type="text"
              value={formData.bankName}
              onChange={(e) => setFormData({ ...formData, bankName: e.target.value.toUpperCase() })}
              maxLength={23}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              placeholder="Bank Name"
            />
            <p className="mt-1 text-xs text-gray-500">Your originating bank's name (max 23 characters)</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Bank Routing Number *</label>
            <input
              type="text"
              value={formData.bankRoutingNumber}
              onChange={(e) => handleRoutingChange(e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 ${routingError ? 'border-red-300' : 'border-gray-300'}`}
              placeholder="XXXXXXXXX"
              maxLength={9}
              inputMode="numeric"
            />
            {routingError ? (
              <p className="mt-1 text-xs text-red-600">{routingError}</p>
            ) : (
              <p className="mt-1 text-xs text-gray-500">9-digit ABA routing number (validated with check digit)</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Effective Entry Date *</label>
            <input
              type="date"
              value={formData.effectiveEntryDate}
              onChange={(e) => handleDateChange(e.target.value)}
              min={new Date().toISOString().split('T')[0]}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 ${dateWarning ? 'border-yellow-400' : 'border-gray-300'}`}
            />
            {dateWarning ? (
              <p className="mt-1 text-xs text-yellow-700">{dateWarning}</p>
            ) : (
              <p className="mt-1 text-xs text-gray-500">The date ACH transfers should post (must be a business day)</p>
            )}
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
            <p className="text-sm text-yellow-800">
              <strong>Important:</strong> This NACHA file contains sensitive banking information. Handle it securely and upload it to your bank's ACH processing system.
            </p>
          </div>
        </div>

        <div className="flex gap-3 pt-4 mt-4 border-t">
          <Button type="button" variant="secondary" className="flex-1" onClick={onClose} disabled={isExporting}>
            Cancel
          </Button>
          <Button onClick={handleExport} className="flex-1" disabled={isExporting}>
            {isExporting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                Exporting...
              </>
            ) : (
              <>
                <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
                Download NACHA File
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default DistributionsPage;
